import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, CalendarPlus, Check, ChevronDown, Copy, Download, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { STORY_CAPTIONS, TEEN_STORIES } from "./Marketing";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useSite } from "../context/SiteContext";

const STORAGE_KEY = "letgo:social-planner";

// Combine general stories + teen stress series into one chronological feed.
const ALL_POSTS = [
  ...STORY_CAPTIONS.map((c, i) => ({
    id: `gen-${i + 1}`,
    title: c.title,
    tag: c.tag,
    caption: c.caption,
    posterFile: c.posterFile,
    posterAnchor: posterAnchorFromFilename(c.posterFile),
  })),
  ...TEEN_STORIES.map((t) => ({
    id: `teen-${t.id}`,
    title: t.titleStr,
    tag: `Teen · ${t.tag}`,
    caption: t.caption,
    posterFile: `letgo-teen-stress-${t.slug}.png`,
    posterAnchor: `teen-${t.id}`,
  })),
];

// Map a poster filename back to its AssetSection anchor on /marketing.
function posterAnchorFromFilename(filename) {
  const map = {
    "letgo-story-poster-first-session.png": "story-poster",
    "letgo-story-poster-the-pause-between.png": "story-poster-couples",
    "letgo-story-poster-what-the-body-knew.png": "story-poster-trauma",
    "letgo-story-poster-after-the-baby-came-home.png": "story-poster-postpartum",
    "letgo-story-poster-the-exit-she-didnt-take.png": "story-poster-burnout",
    "letgo-story-poster-the-empty-chair.png": "story-poster-grief",
    "letgo-story-poster-la-primera-cita.png": "story-poster-spanish",
    "letgo-story-poster-the-list-at-2am.png": "story-poster-anxiety",
    "letgo-story-poster-she-wouldnt-look-up.png": "story-poster-teens",
    "letgo-story-poster-the-fourth-time.png": "story-poster-recovery",
  };
  return map[filename] || "marketing-kit";
}

function nextMonday(from = new Date()) {
  const d = new Date(from);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  const delta = day === 1 ? 7 : (8 - day) % 7 || 7;
  d.setDate(d.getDate() + delta);
  return d;
}

function fmtDate(d) {
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" });
}
function toISODate(d) {
  return d.toISOString().slice(0, 10);
}

export default function SocialPlanner() {
  const { site } = useSite();
  const [startDate, setStartDate] = useState(() => toISODate(nextMonday()));
  const [cadence, setCadence] = useState("weekly"); // weekly | twice-weekly
  const [posted, setPosted] = useState({});
  const [openId, setOpenId] = useState(ALL_POSTS[0]?.id);

  // Restore state from localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed.startDate) setStartDate(parsed.startDate);
        if (parsed.cadence) setCadence(parsed.cadence);
        if (parsed.posted) setPosted(parsed.posted);
      }
    } catch (_) {}
  }, []);

  // Persist state
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ startDate, cadence, posted }));
    } catch (_) {}
  }, [startDate, cadence, posted]);

  // Compute schedule: date per post
  const schedule = useMemo(() => {
    const start = new Date(`${startDate}T00:00:00`);
    const stepDays = cadence === "twice-weekly" ? 3 : 7;
    return ALL_POSTS.map((p, i) => {
      const d = new Date(start);
      d.setDate(d.getDate() + i * stepDays);
      return { ...p, date: d, step: i + 1 };
    });
  }, [startDate, cadence]);

  const totalPosted = schedule.filter((s) => posted[s.id]).length;
  const lastDate = schedule[schedule.length - 1]?.date;

  const togglePosted = (id) => {
    setPosted((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const copyCaption = (caption, step) => {
    navigator.clipboard.writeText(caption);
    toast.success(`Caption ${String(step).padStart(2, "0")} copied`);
  };

  // Trigger a poster download without leaving the planner. We open the
  // marketing page in a small popup with ?download=<anchor>, which auto-fires
  // the download and then auto-closes the window.
  const downloadPoster = (post) => {
    const url = `/marketing?download=${encodeURIComponent(post.posterAnchor)}`;
    const win = window.open(url, "letgo-download", "width=520,height=420");
    const toastId = toast.loading(`Preparing poster ${String(post.step).padStart(2, "0")}…`);

    // Listen for the postMessage from the popup confirming the download fired,
    // then close the popup and dismiss the toast.
    const handler = (event) => {
      if (event.data?.type === "letgo:downloaded") {
        toast.success(`Poster downloaded · ${post.title}`, { id: toastId });
        window.removeEventListener("message", handler);
        try { win?.close(); } catch (_) {}
      }
    };
    window.addEventListener("message", handler);

    // Hard timeout fallback in case the popup never reports back.
    setTimeout(() => {
      window.removeEventListener("message", handler);
      toast.dismiss(toastId);
      try { win?.close(); } catch (_) {}
    }, 25000);

    // Popup blocked? Fall back to navigation in a new tab.
    if (!win || win.closed) {
      toast.dismiss(toastId);
      window.open(url, "_blank");
    }
  };

  const resetProgress = () => {
    if (window.confirm("Reset all posting progress? This won't delete the posts — just the checkmarks.")) {
      setPosted({});
      toast.success("Progress reset");
    }
  };

  // Build & download an .ics calendar file with all 20 posting dates.
  // Each event = an all-day reminder on the scheduled day + a 9am alert.
  const downloadIcs = () => {
    const pad = (n) => String(n).padStart(2, "0");
    const fmt = (d) => `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
    const fmtUtc = (d) =>
      `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}Z`;
    const now = fmtUtc(new Date());
    const esc = (s) => String(s).replace(/\\/g, "\\\\").replace(/\n/g, "\\n").replace(/,/g, "\\,").replace(/;/g, "\\;");

    const lines = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "PRODID:-//Let Go Wellness//Social Planner//EN",
      "CALSCALE:GREGORIAN",
      "METHOD:PUBLISH",
      "X-WR-CALNAME:Let Go Wellness · Social Planner",
      "X-WR-TIMEZONE:America/New_York",
    ];

    schedule.forEach((post) => {
      const start = post.date;
      const end = new Date(start);
      end.setDate(end.getDate() + 1); // all-day events use exclusive end
      const summary = `LGW Post #${String(post.step).padStart(2, "0")} · ${post.title}`;
      const captionPreview = post.caption.split("\n").slice(0, 3).join(" ").slice(0, 240);
      const description =
        `${captionPreview}…\\n\\n` +
        `Poster file: ${post.posterFile}\\n` +
        `Open the planner: https://letgowelllness.com/socials\\n` +
        `Download the poster: https://letgowelllness.com/marketing#${post.posterAnchor}`;

      lines.push(
        "BEGIN:VEVENT",
        `UID:lgw-social-${post.id}-${fmt(start)}@letgowelllness.com`,
        `DTSTAMP:${now}`,
        `DTSTART;VALUE=DATE:${fmt(start)}`,
        `DTEND;VALUE=DATE:${fmt(end)}`,
        `SUMMARY:${esc(summary)}`,
        `DESCRIPTION:${esc(description)}`,
        `CATEGORIES:Marketing,Social,${esc(post.tag)}`,
        "BEGIN:VALARM",
        "ACTION:DISPLAY",
        `DESCRIPTION:${esc("Post today: " + post.title)}`,
        "TRIGGER:-PT15H", // alert ~9am the day-of (start is midnight)
        "END:VALARM",
        "END:VEVENT"
      );
    });

    lines.push("END:VCALENDAR");
    const ics = lines.join("\r\n");

    const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "letgo-social-planner.ics";
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
    toast.success("Calendar file downloaded — open it to add 20 reminders");
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Navbar />

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 pt-32 pb-12">
        <Link to="/marketing" className="text-sm text-[#9F5C45] hover:text-[#3A5A40] inline-flex items-center gap-2 mb-4" data-testid="back-to-marketing">
          ← Back to marketing kit
        </Link>
        <p className="overline text-[#9F5C45] mb-3">Social Planner</p>
        <h1 className="font-serif font-light text-4xl md:text-6xl text-[#1C1917] tracking-tight">
          Your posting calendar,<br />
          <em className="text-[#3A5A40]">step by step.</em>
        </h1>
        <p className="text-[#57534E] mt-4 max-w-2xl text-base">
          Pick a start date. We'll walk you through every Instagram post — date, caption, and poster — one week at a time. Check each one off as you go.
        </p>
      </section>

      {/* Controls */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 pb-10">
        <div className="bg-[#F5F0E6] border border-[#E2DCD0] rounded-sm p-6 grid grid-cols-1 md:grid-cols-4 gap-6 items-end">
          <div>
            <label className="overline text-[#57534E] block mb-2">Start date</label>
            <div className="flex items-center gap-2 border border-[#E2DCD0] bg-white px-3 py-2.5">
              <Calendar className="w-4 h-4 text-[#3A5A40]" />
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                data-testid="start-date-input"
                className="bg-transparent flex-1 outline-none text-[#1C1917]"
              />
            </div>
          </div>

          <div>
            <label className="overline text-[#57534E] block mb-2">Cadence</label>
            <div className="flex gap-2">
              {[
                { v: "weekly", l: "Weekly" },
                { v: "twice-weekly", l: "Twice / week" },
              ].map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setCadence(opt.v)}
                  data-testid={`cadence-${opt.v}`}
                  className={`flex-1 px-3 py-2.5 text-xs uppercase tracking-[0.22em] transition-colors ${
                    cadence === opt.v
                      ? "bg-[#3A5A40] text-[#FDFBF7]"
                      : "bg-white text-[#1C1917] border border-[#E2DCD0] hover:bg-[#EAE5D9]"
                  }`}
                >
                  {opt.l}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="overline text-[#57534E] block mb-2">Progress</label>
            <div className="bg-white border border-[#E2DCD0] px-3 py-2.5">
              <div className="flex items-center justify-between text-sm">
                <span className="text-[#1C1917] font-medium" data-testid="progress-count">
                  {totalPosted} / {ALL_POSTS.length}
                </span>
                <span className="text-xs text-[#57534E]">
                  {Math.round((totalPosted / ALL_POSTS.length) * 100)}%
                </span>
              </div>
              <div className="mt-2 h-1.5 bg-[#EAE5D9] rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#3A5A40] transition-all duration-500"
                  style={{ width: `${(totalPosted / ALL_POSTS.length) * 100}%` }}
                />
              </div>
            </div>
          </div>

          <div>
            <label className="overline text-[#57534E] block mb-2">Campaign ends</label>
            <div className="bg-white border border-[#E2DCD0] px-3 py-2.5">
              <p className="text-sm text-[#1C1917] font-medium" data-testid="last-date">
                {lastDate ? fmtDate(lastDate) : "—"}
              </p>
              <button
                type="button"
                onClick={resetProgress}
                data-testid="reset-progress"
                className="text-[10px] uppercase tracking-[0.28em] text-[#9F5C45] hover:text-[#3A5A40] mt-1"
              >
                Reset progress
              </button>
            </div>
          </div>
        </div>

        {/* Add to calendar */}
        <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-1">
          <p className="text-sm text-[#57534E]">
            Want a reminder on each posting day? Add the schedule to your phone or Google Calendar.
          </p>
          <button
            type="button"
            onClick={downloadIcs}
            data-testid="download-ics"
            className="inline-flex items-center justify-center gap-2 px-5 py-3 btn-primary text-xs uppercase tracking-[0.22em] self-start sm:self-auto"
          >
            <CalendarPlus className="w-4 h-4" /> Add 20 reminders to calendar (.ics)
          </button>
        </div>
      </section>

      {/* Stepper */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 pb-24">
        <div className="space-y-3">
          {schedule.map((post) => {
            const isOpen = openId === post.id;
            const isPosted = !!posted[post.id];
            return (
              <article
                key={post.id}
                data-testid={`step-${post.step}`}
                className={`border rounded-sm transition-colors ${
                  isPosted ? "border-[#3A5A40]/30 bg-[#F0EFEA]" : "border-[#E2DCD0] bg-white"
                }`}
              >
                {/* Header row */}
                <button
                  type="button"
                  onClick={() => setOpenId(isOpen ? null : post.id)}
                  className="w-full px-5 py-4 flex items-center gap-4 text-left hover:bg-[#FAF6EE] transition-colors"
                  data-testid={`toggle-step-${post.step}`}
                >
                  {/* Step number / posted check */}
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                      isPosted ? "bg-[#3A5A40] text-[#FDFBF7]" : "bg-[#EAE5D9] text-[#3A5A40]"
                    }`}
                  >
                    {isPosted ? <Check className="w-4 h-4" /> : String(post.step).padStart(2, "0")}
                  </div>

                  {/* Date + title */}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs uppercase tracking-[0.22em] text-[#9F5C45]">
                      {fmtDate(post.date)} · {post.tag}
                    </p>
                    <h3 className="font-serif text-xl text-[#1C1917] mt-0.5 truncate">{post.title}</h3>
                  </div>

                  {/* Toggle icon */}
                  <ChevronDown
                    className={`w-5 h-5 text-[#57534E] transition-transform ${isOpen ? "rotate-180" : ""}`}
                  />
                </button>

                {/* Expanded body */}
                {isOpen && (
                  <div className="px-5 pb-6 pt-2 border-t border-[#E2DCD0] grid grid-cols-1 md:grid-cols-3 gap-6" data-testid={`step-body-${post.step}`}>
                    {/* Caption */}
                    <div className="md:col-span-2">
                      <p className="overline text-[#57534E] mb-2">Caption</p>
                      <pre className="whitespace-pre-wrap text-sm text-[#1C1917] leading-relaxed font-sans bg-[#FDFBF7] border border-[#E2DCD0] p-4 max-h-72 overflow-y-auto">
                        {post.caption}
                      </pre>
                      <button
                        type="button"
                        onClick={() => copyCaption(post.caption, post.step)}
                        data-testid={`copy-step-${post.step}`}
                        className="mt-3 inline-flex items-center gap-2 px-4 py-2 btn-primary text-[10px] uppercase tracking-[0.28em]"
                      >
                        <Copy className="w-3 h-3" /> Copy caption
                      </button>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-3">
                      <p className="overline text-[#57534E]">Actions</p>

                      <button
                        type="button"
                        onClick={() => downloadPoster(post)}
                        data-testid={`download-poster-${post.step}`}
                        className="inline-flex items-center justify-center gap-2 px-4 py-3 btn-primary text-xs uppercase tracking-[0.22em]"
                      >
                        <Download className="w-4 h-4" /> Download poster
                      </button>

                      <Link
                        to={`/marketing#${post.posterAnchor}`}
                        data-testid={`open-poster-${post.step}`}
                        className="inline-flex items-center justify-center gap-2 px-4 py-3 btn-ghost text-xs uppercase tracking-[0.22em]"
                      >
                        <ExternalLink className="w-4 h-4" /> View on marketing kit
                      </Link>

                      <a
                        href="https://instagram.com"
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`open-instagram-${post.step}`}
                        className="inline-flex items-center justify-center gap-2 px-4 py-3 btn-ghost text-xs uppercase tracking-[0.22em]"
                      >
                        <ExternalLink className="w-4 h-4" /> Open Instagram
                      </a>

                      <button
                        type="button"
                        onClick={() => togglePosted(post.id)}
                        data-testid={`toggle-posted-${post.step}`}
                        className={`inline-flex items-center justify-center gap-2 px-4 py-3 text-xs uppercase tracking-[0.22em] transition-colors ${
                          isPosted
                            ? "bg-[#3A5A40] text-[#FDFBF7] hover:bg-[#2E4A33]"
                            : "bg-white border border-[#E2DCD0] text-[#1C1917] hover:bg-[#EAE5D9]"
                        }`}
                      >
                        <Check className="w-4 h-4" />
                        {isPosted ? "Posted ✓" : "Mark as posted"}
                      </button>

                      <p className="text-[10px] uppercase tracking-[0.28em] text-[#57534E] mt-1">
                        Poster file
                      </p>
                      <p className="text-xs text-[#57534E] font-mono break-all">{post.posterFile}</p>
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </div>

        <div className="mt-12 p-6 bg-[#F5F0E6] border border-[#E2DCD0] text-sm text-[#57534E] leading-relaxed">
          <p className="overline text-[#9F5C45] mb-2">A note on consistency</p>
          <p>
            Mental-health content does its best work when it shows up reliably. One post a week is enough — twice a week is plenty. You don't need a marketing team. You just need a plan, and you have one now.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
